import * as algorithms from '@antv/algorithm'
import domToImage from 'dom-to-image'
import EventEmitter3 from 'eventemitter3'
import fileSaver from 'file-saver'
import { cloneDeep, get, isEqual, merge, pick } from 'lodash-es'
import { InterpreterFrom, interpret } from 'xstate'

import { DragNode, ResizeObserverExtension, Viewport } from '../extensions'
import { GraphStoreContext, createGraphStore } from '../store'
import {
  BASE_EVENTS,
  EVENT_NAME,
  EdgeData,
  ElementIdentifier,
  EventInfo,
  EventNameMapping,
  GraphCoreOptions,
  GraphEvent,
  GraphEventType,
  GraphHooks,
  GraphOptions,
  LayoutConfig,
  NodeData,
} from '../types'
import type { Renderer } from '../types/renderer'
import { isMouseInElement } from '../utils/element'
import { Edge } from './edge'
import { Node } from './node'
import type { Plugin } from './plugin'
import { Stack } from './stack'

export type GraphOptionsHasDefaultValue = Pick<
  Required<GraphOptions>,
  'elementsSelectable' | 'animate' | 'animateOptions'
> & {
  animateOptions: {
    duration: number
  }
}

export class Graph<
  N extends Node = Node,
  E extends Edge = Edge
> extends EventEmitter3<EVENT_NAME> {
  /**
   * 图算法
   */
  static Algorithms = algorithms

  /**
   * 状态机
   */
  service: InterpreterFrom<ReturnType<typeof createGraphStore>>
  /**
   * 是否已销毁，在异步任务中可以避免被调用
   */
  destroyed = false
  /**
   * 插件集
   */
  plugins: Map<string, Plugin> = new Map<string, Plugin>()
  /**
   * 事件
   */
  events: GraphOptions['events']
  /**
   * 视口
   */
  viewport: Viewport
  /**
   * 栈
   */
  stack: Stack
  /**
   * 节点拖拽插件
   */
  dragNodeExtension: Plugin
  /**
   * 渲染器协议
   *
   * 为了兼容不同的渲染器，提供了一个接口，用于渲染器协议。
   * 在处理和渲染层相关的逻辑时，可以通过渲染器协议来获取DOM。
   * 例如计算包围盒。
   */
  renderer!: Renderer

  constructor(
    options: GraphOptions,
    layoutContext: Pick<GraphStoreContext, 'graphType'>
  ) {
    super()

    const configure = this.getComputedOptions(options)
    const {
      data,
      layout,
      width,
      height,
      animate,
      animateOptions = {
        duration: 300,
      },
      minZoom,
      maxZoom,
      defaultZoom,
      defaultPosition,
      defaultNode,
      defaultEdge,
      nodeDraggable,
      elementsSelectable,
      events,
      preventScrolling,
      plugins,
      translateExtent,
      fitView,
      fitViewPadding,
      fitCenter,
      disableZoom,
    } = configure

    this.events = events
    this.viewport = new Viewport(this, {
      minZoom,
      maxZoom,
      defaultZoom,
      defaultPosition,
      translateExtent,
      preventScrolling,
      fitView,
      fitCenter,
      fitViewPadding,
      disableZoom,
    })
    this.stack = new Stack(this)
    this.dragNodeExtension = DragNode({
      nodeDraggable,
    })

    const resizeObserverExtension = ResizeObserverExtension()
    const observedContextValue = ['layoutConfig', 'graphData'] as Array<
      keyof GraphStoreContext
    >

    this.service = interpret(
      createGraphStore({
        emit: this.emit,
        context: {
          ...layoutContext,
          positions: {},
          graphData: data,
          layoutConfig: layout,
          width: 0,
          height: 0,
          configure: {
            animate,
            animateOptions,
            defaultNode,
            defaultEdge,
            elementsSelectable,
          },
        },
      })
    ).onChange((context, prevContext) => {
      observedContextValue.some((key) => {
        if (!isEqual(context[key], prevContext?.[key])) {
          this.stack.push(
            pick(context, observedContextValue) as GraphStoreContext
          )

          return true
        }

        return false
      })
    })
    this.service.start()

    this.once(EVENT_NAME.afterinit, () => {
      this.initEvents()
      this.initPlugin(plugins, [
        this.dragNodeExtension,
        resizeObserverExtension,
      ])
      this.changeSize(width, height, false)
    })
  }

  /**
   * 图类型
   */
  get graphType() {
    return this.service.state.context.graphType
  }

  /**
   * 原始数据
   */
  get data() {
    return this.service.state.context.graphData
  }

  /**
   * 节点列表
   */
  get nodes(): N[] {
    return this.service.state.context.nodes as unknown as N[]
  }

  /**
   * 边列表
   */
  get edges(): E[] {
    return this.service.state.context.edges as unknown as E[]
  }

  /**
   * 节点集合
   */
  get nodeMap() {
    return this.nodes.reduce<{ [key: ElementIdentifier]: Node }>(
      (map, node) => {
        map[node.id] = node

        return map
      },
      {}
    )
  }

  /**
   * 边集合
   */
  get edgeMap() {
    return this.edges.reduce<{ [key: ElementIdentifier]: Edge }>(
      (map, edge) => {
        map[edge.id] = edge

        return map
      },
      {}
    )
  }

  /**
   * 节点位置
   */
  get positions() {
    return this.service.state.context.positions
  }

  /**
   * 画布宽度
   */
  get width() {
    return this.service.state.context.width
  }

  /**
   * 画布高度
   */
  get height() {
    return this.service.state.context.height
  }

  /**
   * 偏移量
   */
  get transform() {
    return this.service.state.context.transform
  }

  /**
   * 布局配置
   */
  get layoutConfig() {
    return this.service.state.context.layoutConfig
  }

  /**
   * 动画开关
   */
  get animate() {
    return this.service.state.context.configure.animate
  }

  /**
   * 动画配置
   */
  get animateOptions() {
    return this.service.state.context.configure.animateOptions
  }

  /**
   * 默认节点配置
   */
  get defaultNode() {
    return this.service.state.context.configure.defaultNode
  }

  /**
   * 默认边配置
   */
  get defaultEdge() {
    return this.service.state.context.configure.defaultEdge
  }

  /**
   * 是否在布局中
   */
  get isInLayout() {
    return get(this.service.state, ['value', 'ready']) === 'layout'
  }

  /**
   * 获取布局类型
   */
  get layoutType() {
    return this.layoutConfig?.type
  }

  /**
   * 挂载渲染器
   *
   * @param renderer
   */
  mountRenderer(renderer: Renderer) {
    this.renderer = renderer

    this.service.send('INITIALIZE')
  }

  /**
   * 装载插件
   */
  usePlugin = (plugin: Plugin) => {
    this.initPlugin([plugin])
  }

  /**
   * 更新画布位置
   */
  updateTransform = (transform: [number, number, number]) => {
    this.service.send({
      type: 'UPDATE_TRANSFORM',
      data: {
        transform,
      },
    })
  }

  /**
   * 更新画布大小
   */
  changeSize = (width?: number, height?: number, emitEvent = true) => {
    if (this.destroyed) {
      return
    }

    const { width: rectWidth, height: rectHeight } =
      this.renderer.getContainerRect()
    const finalWidth = width ?? rectWidth
    const finalHeight = height ?? rectHeight

    if (finalWidth && finalHeight) {
      this.service.send({
        type: 'UPDATE_SIZE',
        data: {
          width: finalWidth,
          height: finalHeight,
          emitEvent,
        },
      })

      // 同步容器大小
      if (width != null || height != null) {
        this.renderer.updateContainerSize(width, height)
      }
    }
  }

  /**
   * 启用动画
   */
  enableAnimate = () => {
    this.updateConfigure({
      animate: true,
    })
  }

  /**
   * 禁用动画
   */
  disableAnimate = () => {
    this.updateConfigure({
      animate: false,
    })
  }

  /**
   * 变更动画配置
   *
   * @param animateOptions
   */
  changeAnimateOptions = (
    animateOptions: GraphCoreOptions['animateOptions']
  ) => {
    this.updateConfigure({
      animateOptions: merge(this.animateOptions, animateOptions),
    })
  }

  /**
   * 变更最小缩放比例
   *
   * @param minZoom
   */
  changeMinZoom = (minZoom: number) => {
    this.viewport.options.minZoom = minZoom
  }

  /**
   * 变更最大缩放比例
   *
   * @param maxZoom
   */
  changeMaxZoom = (maxZoom: number) => {
    this.viewport.options.maxZoom = maxZoom
  }

  /**
   * 变更画布最大移动范围
   *
   * @param translateExtent
   */
  changeTranslateExtent = (
    translateExtent: [[number, number], [number, number]]
  ) => {
    this.viewport.options.translateExtent = translateExtent
  }

  /**
   * 启用自适应
   */
  enableFitView = () => {
    this.viewport.options.fitView = true
  }

  /**
   * 禁用自适应
   */
  disableFitView = () => {
    this.viewport.options.fitView = false
  }

  /**
   * 变更自适应间距比例
   *
   * @param fitViewPadding
   */
  updateFitViewPadding = (fitViewPadding: [number, number]) => {
    this.viewport.options.fitViewPadding = fitViewPadding
  }

  /**
   * 启用自动居中
   */
  enableFitCenter = () => {
    this.viewport.options.fitCenter = true
  }

  /**
   * 禁用自动居中
   */
  disableFitCenter = () => {
    this.viewport.options.fitCenter = false
  }

  /**
   * 启用缩放
   */
  enableZoom = () => {
    this.viewport.options.disableZoom = true
  }

  /**
   * 禁用自动居中
   */
  disableZoom = () => {
    this.viewport.options.disableZoom = false
  }

  /**
   * 启用节点拖拽
   */
  enableNodeDraggable = () => {
    this.dragNodeExtension.setOptions({
      nodeDraggable: true,
    })
  }

  /**
   * 禁用节点拖拽
   */
  disableNodeDraggable = () => {
    this.dragNodeExtension.setOptions({
      nodeDraggable: false,
    })
  }

  /**
   * 启用元素选中
   */
  enableElementSelectable = () => {
    this.updateConfigure({
      elementsSelectable: true,
    })
  }

  /**
   * 禁用元素选中
   */
  disableElementSelectable = () => {
    this.updateConfigure({
      elementsSelectable: false,
    })
  }

  /**
   * 以当前配置执行布局
   */
  layout = (): void => {
    this.service.send({
      type: 'EXECUTE_LAYOUT',
    })
  }

  /**
   * 更新布局
   */
  updateLayout = (layoutConfig: LayoutConfig) => {
    this.service.send({
      type: 'UPDATE_LAYOUT_CONFIG',
      data: {
        layoutConfig,
      },
    })
  }

  /**
   * 派发事件
   */
  override emit = (
    name: EVENT_NAME,
    { type, composeEventName = true, ...rest }: EventInfo = {}
  ) => {
    const composedEventName =
      type != null && composeEventName
        ? EVENT_NAME[`${type}:${name}` as unknown as keyof typeof EVENT_NAME]
        : name
    const graphEvent = this.makeEvent({
      ...rest,
      type,
      name: composedEventName,
    })

    super.emit(composedEventName, graphEvent, this)

    return true
  }

  /**
   * 派发元素事件
   *
   * @param name 基础事件，例如 drag, click
   * @param event
   */
  emitElementEvent = (name: EVENT_NAME, event: Event) => {
    const eventInfo = this.detectElementEvent(event, name)

    if (eventInfo) {
      // 派发元素事件
      this.emit(name, eventInfo)
      // 派发基础事件
      this.emit(name, {
        ...eventInfo,
        composeEventName: false,
      })
    }
  }

  /**
   * 检测事件元素
   */
  detectElementEvent = (event: Event, eventName: EVENT_NAME) => {
    const node = this.nodes.find((node) =>
      node.element?.contains(event.target as HTMLElement | null)
    )

    if (node) {
      if (
        eventName === 'mouseout' &&
        node.element &&
        isMouseInElement(node.element, event as MouseEvent)
      ) {
        return
      }

      return {
        type: GraphEventType.node,
        item: node,
        name: eventName,
        originalEvent: event,
      }
    }

    const edge = this.edges.find((edge) =>
      edge.element?.contains(event.target as HTMLElement | null)
    )

    if (edge) {
      if (
        eventName === 'mouseout' &&
        edge.element &&
        !isMouseInElement(edge.element, event as MouseEvent)
      ) {
        return
      }

      return {
        type: GraphEventType.edge,
        item: edge,
        name: eventName,
        originalEvent: event,
      }
    }

    if (this.renderer.container.contains(event.target as HTMLElement | null)) {
      return {
        type: GraphEventType.canvas,
        name: eventName,
        originalEvent: event,
      }
    }

    return {
      name: eventName,
      originalEvent: event,
    }
  }

  /**
   * 获取当前的缩放比例
   */
  getZoom = (...params: Parameters<Graph['viewport']['getZoom']>) => {
    return this.viewport.getZoom(...params)
  }

  /**
   * 缩放到指定比例，并以指定位置为中心
   */
  zoomTo = (...params: Parameters<Graph['viewport']['zoomTo']>) =>
    this.viewport.zoomTo(...params)

  /**
   * 放大
   */
  zoomIn = (...params: Parameters<Graph['viewport']['zoomIn']>) =>
    this.viewport.zoomIn(...params)

  /**
   * 缩小
   */
  zoomOut = (...params: Parameters<Graph['viewport']['zoomOut']>) =>
    this.viewport.zoomOut(...params)

  /**
   * 根据配置居中或自适应
   */
  fitViewport = (...params: Parameters<Graph['viewport']['fitViewport']>) =>
    this.viewport.fitViewport(...params)

  /**
   * 自适应
   */
  fitView(...params: Parameters<Graph['viewport']['fitView']>) {
    return this.viewport.fitView(...params)
  }

  /**
   * 居中 不缩放
   */
  fitCenter = (...params: Parameters<Graph['viewport']['fitCenter']>) =>
    this.viewport.fitCenter(...params)

  /**
   * 聚焦节点
   * @param params
   */
  focusNode = (...params: Parameters<Graph['viewport']['focusNode']>) =>
    this.viewport.focusNode(...params)

  /**
   * 切换全屏
   * @param params
   */
  toggleFullScreen = (
    ...params: Parameters<Graph['viewport']['toggleFullScreen']>
  ) => this.viewport.toggleFullScreen(...params)

  /**
   * 导出图片
   * @param type
   * @param name
   */
  exportImage = async (
    type: 'png' | 'svg' | 'jpeg',
    name = '导出',
    padding = 60
  ) => {
    if (this.renderer.zoomContainer == null) {
      return
    }

    const { width, height } = this.renderer.getBBox()
    const containerWidth = width + padding
    const containerHeight = height + padding

    const container = this.renderer.container.cloneNode(true) as SVGElement
    const zoomContainer = container.querySelector(
      `.${this.renderer.zoomContainer.getAttribute('class')}`
    )

    if (zoomContainer == null) {
      throw new Error('can not find zoom container')
    }

    container.style.position = 'fixed'
    container.style.left = '0'
    container.style.top = '0'
    container.style.zIndex = '-1'
    container.style.width = `${containerWidth}px`
    container.style.height = `${containerHeight}px`
    zoomContainer.setAttribute('transform', 'translate(0, 0) scale(1)')
    document.body.appendChild(container)
    zoomContainer.setAttribute(
      'transform',
      `translate(${(width + padding) / 2 + Node.DEFAULT_NODE_SIZE[0] / 4}, ${
        (height + padding) / 2 + Node.DEFAULT_NODE_SIZE[0] / 4
      }) scale(1)`
    )

    try {
      const fileName = `${name}.${type}`

      switch (type) {
        case 'png':
        case 'jpeg':
          fileSaver.saveAs(await domToImage.toBlob(container), fileName)
          break
        case 'svg':
          fileSaver.saveAs(await domToImage.toSvg(container), fileName)
          break
      }
    } catch (error) {
      console.error(error)
    }

    document.body.removeChild(container)
  }

  /**
   * 获取图中所有节点的实例
   */
  getNodes = () => this.nodes

  /**
   * 获取图中所有边的实例
   */
  getEdges = () => this.edges

  /**
   * 根据具体规则查找单个节点
   *
   * @param predicate
   */
  findNodeBy = <N = Node>(predicate: (node: Node) => boolean): N | undefined =>
    this.nodes.find(predicate) as N | undefined

  /**
   * 获取节点
   */
  findNodeById = <N = Node>(id: Node['id']): N | undefined =>
    this.nodes.find((node) => node.id === id) as N | undefined

  /**
   * 获取边
   */
  findEdgeById = <E = Edge>(id: Edge['id']): E | undefined =>
    this.edges.find((edge) => edge.id === id) as E | undefined

  /**
   * 导出数据
   */
  exportData = () =>
    cloneDeep({
      nodes: this.nodes,
      edges: this.edges,
    })

  /**
   * 更新配置
   */
  updateConfigure = (configure: Partial<GraphStoreContext['configure']>) => {
    this.service.send({
      type: 'UPDATE_CONFIGURE',
      data: {
        configure,
      },
    })
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.destroyed = true
    this.emit(EVENT_NAME.destroy)
    this.removeAllListeners()
  }

  /**
   * 更新节点
   */
  updateNode(nodeOrNodeId: ElementIdentifier | Node, nodeData: NodeData) {
    const id = Node.isNode(nodeOrNodeId) ? nodeOrNodeId.id : nodeOrNodeId

    this.service.send({
      type: 'UPDATE_NODE_PROPERTIES',
      data: {
        id,
        nodeData,
      },
    })
  }

  /**
   * 更新边
   */
  updateEdge(edgeOrEdgeId: ElementIdentifier | Edge, edgeData: EdgeData) {
    const id = Edge.isEdge(edgeOrEdgeId) ? edgeOrEdgeId.id : edgeOrEdgeId

    this.service.send({
      type: 'UPDATE_EDGE_PROPERTIES',
      data: {
        id,
        edgeData,
      },
    })
  }

  /**
   * 设置节点集合状态
   *
   * @param state
   * @param ids
   * @param enable
   */
  setNodesState = (
    state: string,
    ids?: ElementIdentifier[],
    enable?: boolean
  ) => {
    this.service.send({
      type: 'SET_NODES_STATE',
      data: {
        state,
        enable,
        ids,
      },
    })
  }

  /**
   * 设置边集合状态
   *
   * @param state
   * @param ids
   * @param enable
   */
  setEdgesState = (
    state: string,
    ids?: ElementIdentifier[],
    enable?: boolean
  ) => {
    this.service.send({
      type: 'SET_EDGES_STATE',
      data: {
        state,
        enable,
        ids,
      },
    })
  }

  /**
   * 清除所有节点指定状态
   *
   * @param states
   */
  clearNodesStates = (
    states?: string | string[],
    ids?: ElementIdentifier[]
  ) => {
    this.service.send({
      type: 'CLEAR_NODES_STATES',
      data: {
        states,
        ids,
      },
    })
  }

  /**
   * 清除所有边指定状态
   *
   * @param states
   */
  clearEdgesStates = (
    states?: string | string[],
    ids?: ElementIdentifier[]
  ) => {
    this.service.send({
      type: 'CLEAR_NODES_STATES',
      data: {
        states,
      },
    })
  }

  /**
   * 挂载事件
   */
  protected applyGraphHooks = (hooks: GraphHooks) => {
    Object.entries(hooks).forEach(([eventName, handler]) => {
      const eventType: EVENT_NAME | undefined =
        EventNameMapping[eventName as keyof typeof EventNameMapping]

      if (eventType) {
        this.on(eventType, handler)
      }
    })
  }

  /**
   * 获取默认配置
   */
  protected getDefaultOptions = (): GraphOptionsHasDefaultValue => ({
    animate: true,
    animateOptions: {
      duration: 300,
    },
    elementsSelectable: true,
  })

  /**
   * 获取合并后的配置
   *
   * @param options
   */
  protected getComputedOptions = (options: GraphOptions) =>
    merge({
      ...this.getDefaultOptions(),
      ...options,
    }) as GraphOptions & GraphOptionsHasDefaultValue

  /**
   * 初始化插件
   *
   */
  protected initPlugin = (
    plugins: GraphOptions['plugins'] = [],
    builtin: GraphOptions['plugins'] = []
  ) => {
    ;[...builtin, ...plugins].forEach((plugin) => {
      this.applyGraphHooks(plugin.hooks)

      if (!this.plugins.has(plugin.name)) {
        this.plugins.set(plugin.name, plugin)
        this.emit(EVENT_NAME.pluginmount)

        // 触发插件本身的初始化钩子
        plugin.onPluginInit?.(this)
      } else {
        console.warn(`Plugin ${plugin.name} already exists`)
      }
    })
  }

  /**
   * 初始化事件
   */
  protected initEvents = () => {
    if (this.events) {
      this.applyGraphHooks(this.events)
    }

    this.setupEvents()
  }

  /**
   * 配置单击事件
   *
   */
  protected setupEvents = () => {
    BASE_EVENTS.forEach(
      (name) => {
        this.renderer.container.addEventListener(name, (event) => {
          this.emitElementEvent(name, event)
        })
      },
      {
        capture: true,
      }
    )
  }

  /**
   * 生成内置 Event
   * @protected
   * @param payload
   */
  protected makeEvent = (
    payload: Omit<GraphEvent, 'timeStamp'>
  ): GraphEvent => ({
    ...payload,
    timeStamp: new Date().getTime(),
  })
}
